const LOCAL_API_BASE = "http://localhost/arfseafoods/backend/public/api";
const LIVE_API_BASE = "https://arfseafoods.com/backend/api";
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (process.env.NODE_ENV === "development" ? LOCAL_API_BASE : LIVE_API_BASE);
const TOKEN_KEY = "fishbay.auth.token";

let authToken = "";
let onUnauthorized = null;

try {
  authToken = sessionStorage.getItem(TOKEN_KEY) || "";
} catch (e) {
  authToken = "";
}

export function setAuthToken(token) {
  authToken = token || "";
  try {
    if (authToken) {
      sessionStorage.setItem(TOKEN_KEY, authToken);
    } else {
      sessionStorage.removeItem(TOKEN_KEY);
    }
  } catch (e) {
    // ignore storage errors
  }
}

export function getAuthToken() {
  return authToken;
}

export function getApiBaseRoot() {
  return new URL(API_BASE, window.location.origin).toString().replace(/\/api\/?$/, "");
}

export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

async function request(path, options = {}) {
  const config = { method: "GET", ...options };
  const headers = { Accept: "application/json", ...(config.headers || {}) };
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  if (config.body !== undefined && config.body !== null) {
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
    if (typeof config.body !== "string") {
      config.body = JSON.stringify(config.body);
    }
  }

  const response = await fetch(`${API_BASE}${path}`, { ...config, headers });
  const text = await response.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (e) {
      data = text;
    }
  }

  if (!response.ok) {
    if (response.status === 401 && typeof onUnauthorized === "function") {
      onUnauthorized();
    }
    const message = data && typeof data === "object" && data.error ? data.error : response.statusText;
    throw new Error(message || "Request failed");
  }

  return data;
}

export async function getSuppliers() {
  return request("/suppliers");
}

export async function getCustomers() {
  return request("/customers");
}

export async function searchLocationOptions(query, sessionToken = "") {
  const trimmed = String(query || "").trim();
  if (!trimmed) {
    return { results: [], provider: "", attribution: "" };
  }
  const suffix = sessionToken
    ? `&sessionToken=${encodeURIComponent(sessionToken)}`
    : "";
  return request(`/location-search?q=${encodeURIComponent(trimmed)}${suffix}`);
}

export async function getLocationOptionDetails(placeId, sessionToken = "") {
  const trimmedPlaceId = String(placeId || "").trim();
  if (!trimmedPlaceId) {
    throw new Error("Missing place id");
  }
  const suffix = sessionToken
    ? `?sessionToken=${encodeURIComponent(sessionToken)}`
    : "";
  return request(`/location-search/place/${encodeURIComponent(trimmedPlaceId)}${suffix}`);
}

export async function getLocationMapConfig() {
  return request("/location-search/config");
}

export async function reverseGeocodeLocation(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    throw new Error("Valid latitude and longitude are required.");
  }
  return request(
    `/location-search/reverse?latitude=${encodeURIComponent(lat)}&longitude=${encodeURIComponent(lng)}`
  );
}

export async function login(username, password) {
  const data = await request("/login", { method: "POST", body: { username, password } });
  if (data && data.token) {
    setAuthToken(data.token);
  }
  return data;
}

export async function logout() {
  try {
    await request("/logout", { method: "POST" });
  } finally {
    setAuthToken("");
  }
}

export async function getCurrentUser() {
  const data = await request("/me");
  return data && data.user ? data.user : null;
}

export async function getUsers() {
  return request("/users");
}

export async function createUser(user) {
  return request("/users", { method: "POST", body: user });
}

export async function updateUser(id, updates) {
  return request(`/users/${id}`, { method: "PUT", body: updates });
}

export async function addSupplier(supplier) {
  return request("/suppliers", { method: "POST", body: supplier });
}

export async function updateSupplier(id, supplier) {
  return request(`/suppliers/${id}`, { method: "PUT", body: supplier });
}

export async function addCustomer(customer) {
  return request("/customers", { method: "POST", body: customer });
}

export async function updateCustomer(id, customer) {
  return request(`/customers/${id}`, { method: "PUT", body: customer });
}

export async function removeSupplier(id) {
  return request(`/suppliers/${id}`, { method: "DELETE" });
}

export async function removeCustomer(id) {
  return request(`/customers/${id}`, { method: "DELETE" });
}

export async function getCustomerByPhone(phone) {
  return request(`/customers/phone/${encodeURIComponent(phone)}`);
}

export async function getItems() {
  return request("/items");
}

export async function addItem(item) {
  return request("/items", { method: "POST", body: item });
}

export async function updateItem(id, item) {
  return request(`/items/${id}`, { method: "PUT", body: item });
}

export async function removeItem(id) {
  return request(`/items/${id}`, { method: "DELETE" });
}

export async function getInwardEntries() {
  return request("/inward-entries");
}

export async function addInwardEntry(entry) {
  return request("/inward-entries", { method: "POST", body: entry });
}

export async function updateInwardEntry(entryId, entry) {
  return request(`/inward-entries/${entryId}`, { method: "PUT", body: entry });
}

export async function updateInwardEntryPaymentStatus(entryId, paymentDone) {
  return request(`/inward-entries/${entryId}/payment-status`, { method: "PUT", body: { paymentDone: !!paymentDone } });
}

export async function updateInwardEntryPaymentType(entryId, paymentType) {
  return request(`/inward-entries/${entryId}/payment-type`, { method: "PUT", body: { paymentType } });
}

export async function deleteInwardEntry(entryId) {
  return request(`/inward-entries/${entryId}`, { method: "DELETE" });
}

export async function addInwardPayment(entryId, payment) {
  return request(`/inward-entries/${entryId}/payments`, { method: "POST", body: payment });
}

export async function updateInwardPayment(paymentId, payment) {
  return request(`/inward-payments/${paymentId}`, { method: "PUT", body: payment });
}

export async function deleteInwardPayment(paymentId) {
  return request(`/inward-payments/${paymentId}`, { method: "DELETE" });
}

export async function getSalesEntries() {
  return request("/sales-entries");
}

export async function addSalesEntry(entry) {
  return request("/sales-entries", { method: "POST", body: entry });
}

export async function updateSalesEntry(entryId, entry) {
  return request(`/sales-entries/${entryId}`, { method: "PUT", body: entry });
}

export async function updateSalesEntryPaymentStatus(entryId, paymentDone) {
  return request(`/sales-entries/${entryId}/payment-status`, { method: "PUT", body: { paymentDone: !!paymentDone } });
}

export async function updateSalesEntryPaymentType(entryId, paymentType) {
  return request(`/sales-entries/${entryId}/payment-type`, { method: "PUT", body: { paymentType } });
}

export async function updateSalesEntryDeliveryBoy(entryId, deliveryBoyId) {
  return request(`/sales-entries/${entryId}/delivery-boy`, { method: "PUT", body: { deliveryBoyId } });
}

export async function deleteSalesEntry(entryId) {
  return request(`/sales-entries/${entryId}`, { method: "DELETE" });
}

export async function addSalesPayment(entryId, payment) {
  return request(`/sales-entries/${entryId}/payments`, { method: "POST", body: payment });
}

export async function updateSalesPayment(paymentId, payment) {
  return request(`/sales-payments/${paymentId}`, { method: "PUT", body: payment });
}

export async function deleteSalesPayment(paymentId) {
  return request(`/sales-payments/${paymentId}`, { method: "DELETE" });
}

export async function getBillActionRequests() {
  return request("/bill-action-requests");
}

export async function getMyBillActionRequests() {
  return request("/bill-action-requests/mine");
}

export async function createBillActionRequest(entryId, action, note) {
  return request("/bill-action-requests", { method: "POST", body: { entryId, action, note } });
}

export async function reviewBillActionRequest(requestId, status) {
  return request(`/bill-action-requests/${requestId}`, { method: "PUT", body: { status } });
}

export async function saveReceiptHtml(html) {
  return request("/receipts", { method: "POST", body: { html } });
}

export async function getSupplierLedger() {
  return request("/dashboard/supplier-ledger");
}

export async function getSupplierLedgerDetails(supplierName) {
  return request(`/dashboard/supplier-ledger/${encodeURIComponent(supplierName || "")}`);
}

export async function addSupplierPayment(supplierName, payment) {
  return request(`/dashboard/supplier-ledger/${encodeURIComponent(supplierName || "")}/payments`, {
    method: "POST",
    body: payment,
  });
}

export async function getMiscExpenses() {
  return request("/misc-expenses");
}

export async function addMiscExpense(expense) {
  return request("/misc-expenses", { method: "POST", body: expense });
}

export async function updateMiscExpense(expenseId, expense) {
  return request(`/misc-expenses/${expenseId}`, { method: "PUT", body: expense });
}

export async function deleteMiscExpense(expenseId) {
  return request(`/misc-expenses/${expenseId}`, { method: "DELETE" });
}

export async function getExpenseMasters() {
  return request("/expense-masters");
}

export async function addExpenseMaster(expenseMaster) {
  return request("/expense-masters", { method: "POST", body: expenseMaster });
}

export async function updateExpenseMaster(expenseMasterId, expenseMaster) {
  return request(`/expense-masters/${expenseMasterId}`, { method: "PUT", body: expenseMaster });
}

export async function deleteExpenseMaster(expenseMasterId) {
  return request(`/expense-masters/${expenseMasterId}`, { method: "DELETE" });
}

export async function getEmployeeMasters() {
  return request("/employee-masters");
}

export async function addEmployeeMaster(employeeMaster) {
  return request("/employee-masters", { method: "POST", body: employeeMaster });
}

export async function updateEmployeeMaster(employeeMasterId, employeeMaster) {
  return request(`/employee-masters/${employeeMasterId}`, { method: "PUT", body: employeeMaster });
}

export async function deleteEmployeeMaster(employeeMasterId) {
  return request(`/employee-masters/${employeeMasterId}`, { method: "DELETE" });
}

export async function getDeliveryBoys() {
  return request("/delivery-boys");
}

export async function getDeliveryBoyOptions() {
  return request("/delivery-boys/options");
}

export async function addDeliveryBoy(deliveryBoy) {
  return request("/delivery-boys", { method: "POST", body: deliveryBoy });
}

export async function updateDeliveryBoy(deliveryBoyId, deliveryBoy) {
  return request(`/delivery-boys/${deliveryBoyId}`, { method: "PUT", body: deliveryBoy });
}

export async function deleteDeliveryBoy(deliveryBoyId) {
  return request(`/delivery-boys/${deliveryBoyId}`, { method: "DELETE" });
}

export async function getEmployeeAttendance(weekStart) {
  const suffix = weekStart ? `?weekStart=${encodeURIComponent(weekStart)}` : "";
  return request(`/employee-attendance${suffix}`);
}

export async function saveEmployeeAttendanceWeek(payload) {
  return request("/employee-attendance", { method: "POST", body: payload });
}

export async function saveEmployeeAttendanceMonth(payload) {
  return saveEmployeeAttendanceWeek(payload);
}

export async function getAmountReceivedEntries() {
  return request("/amount-received");
}

export async function addAmountReceivedEntry(entry) {
  return request("/amount-received", { method: "POST", body: entry });
}

export async function updateAmountReceivedEntry(entryId, entry) {
  return request(`/amount-received/${entryId}`, { method: "PUT", body: entry });
}

export async function deleteAmountReceivedEntry(entryId) {
  return request(`/amount-received/${entryId}`, { method: "DELETE" });
}

export const getStore = () => request("/store");
export const updateStore = (data) => request("/store", { method: "PUT", body: data });
